import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTimerStore } from '@/stores/timer';
import { Square } from 'lucide-react';
import { useStopTimer } from '@/hooks/use-time-entries';

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function GlobalTimerWidget() {
  const { isRunning, elapsed, taskKey, projectName } = useTimerStore();
  const stopTimer = useStopTimer();

  if (!isRunning) return null;

  return (
    <div className="flex items-center gap-2">
      {/* Pulsing dot */}
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
      </span>

      {/* Timer display */}
      <span className="font-mono text-sm tabular-nums text-foreground">
        {formatElapsed(elapsed)}
      </span>

      {/* Task/project context */}
      {(taskKey || projectName) && (
        <span className="text-xs text-muted-foreground max-w-[140px] truncate">
          {taskKey && <span className="font-mono text-primary">{taskKey}</span>}
          {taskKey && projectName && ' · '}
          {projectName}
        </span>
      )}

      {/* Stop button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 hover:bg-red-500/10"
            onClick={() => stopTimer.mutate()}
            disabled={stopTimer.isPending}
          >
            <Square className="h-3 w-3 text-red-500" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Stop timer</TooltipContent>
      </Tooltip>
    </div>
  );
}
